import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButtonWrapper } from '~/components/common';
import { ReactComponent as ThumbsDown } from '~/assets/img/icons/thumbs_down.svg';
import { ReactComponent as ThumbsUp } from '~/assets/img/icons/thumbs_up.svg';

export const ChatContainer = styled.div`
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  -webkit-box-flex: 1;
  -webkit-overflow-scrolling: touch;
`;

export const ChatSection = styled.div`
  display: flex;
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  flex-direction: column-reverse;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  ${(props) => props.theme.mixins.scroll.none}
`;

export const ChatBox = styled.ul`
  padding: 0 16px;
  gap: 16px;
`;

export const ChatNameBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
`;

export const ChatName = styled.div`
  ${({ theme }) => css`
    display: block;
    ${theme.fontStyle.text.body16_medium}
    color: ${theme.content.content_light_1};

    span {
      color: ${theme.content.content_light_5};
      ${theme.fontStyle.text.body14_regular}
    }
  `}
`;

export const SystemMessageBox = styled.li`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 16px;
`;

export const ChatList = styled.li<{ isGrouped?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: ${({ isGrouped }) => (isGrouped ? '16px' : '4px')};
`;

export const ChatContentWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    justify-content: flex-start;

    &.info {
      justify-content: center;
    }

    &.other {
      & > .content {
        padding: 8px 16px 8px 12px;
        border-radius: 17px 17px 17px 4px;
        background: ${theme.backgroundColor.bg_light_3_alpha};

        &.first {
          border-radius: 17px 17px 17px 4px;
        }
      }
    }

    &.user {
      flex-direction: row-reverse;

      & > .content {
        padding: 8px 12px 8px 16px;
        border-radius: 17px 17px 4px 17px;
        background: rgba(169, 229, 237, 0.9);

        &.first {
          border-radius: 17px 17px 4px 17px;
        }
      }
    }
  `}
`;

export const Content = styled.div`
  ${({ theme }) => css`
    display: block;
    max-width: calc(100% - 70px);
    /* transition: border-radius 0.1s ease-in-out; */
    color: ${theme.content.content_dark_1};
    line-height: 5px;

    & > p {
      display: block;
      word-break: break-all;
      ${theme.fontStyle.text.body16_medium}

      &.italic {
        color: #afafb6;
        color: ${theme.content.content_dark_3_alpha};
        font-family: 'Noto Sans', sans-serif;
        font-style: italic;
        font-weight: 500;
        font-optical-sizing: auto;
      }
    }
  `}
`;

export const ChatTime = styled.span`
  ${({ theme }) => css`
    display: block;
    color: ${theme.content.hierarchy3};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: -0.1px;
    line-height: 22px;
  `}
`;

export const DotLoaderBox = styled.div`
  ${({ theme }) => css`
    display: flex;
    position: relative;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 34px;
    padding: 8px 16px 8px 12px;
    overflow: hidden;
    border-radius: 17px 17px 17px 4px;
    background: ${theme.backgroundColor.bg_light_3_alpha};
  `}
`;

export const DotLoader = styled.span`
  ${({ theme }) => css`
    /* HTML: <div class="loader"></div> */
    position: relative;
    left: -9999px;
    width: 7px;
    height: 7px;
    animation: dot-falling 1s infinite linear;
    animation-delay: 0.1s;
    border-radius: 50%;
    background-color: ${theme.content.content_dark_3_alpha};
    box-shadow: 9999px 0 0 0 ${theme.content.content_dark_3_alpha};
    color: ${theme.content.content_dark_3_alpha};

    &::before {
      content: '';
      display: inline-block;
      position: absolute;
      top: 0;
      width: 7px;
      height: 7px;
      animation: dot-falling-before 1s infinite linear;
      animation-delay: 0s;
      border-radius: 50%;
      background-color: ${theme.content.content_dark_3_alpha};
      color: ${theme.content.content_dark_3_alpha};
    }

    &::after {
      content: '';
      display: inline-block;
      position: absolute;
      top: 0;
      width: 7px;
      height: 7px;
      animation: dot-falling-after 1s infinite linear;
      animation-delay: 0.2s;
      border-radius: 50%;
      background-color: ${theme.content.content_dark_3_alpha};
      color: ${theme.content.content_dark_3_alpha};
    }

    @keyframes dot-falling {
      0% {
        box-shadow: 9999px -15px 0 0 rgba(218, 218, 211, 0);
      }

      25%,
      50%,
      75% {
        box-shadow: 9999px 0 0 0 ${theme.content.content_dark_3_alpha};
      }

      100% {
        box-shadow: 9999px 15px 0 0 rgba(218, 218, 211, 0);
      }
    }
    @keyframes dot-falling-before {
      0% {
        box-shadow: 9984px -15px 0 0 rgba(218, 218, 211, 0);
      }

      25%,
      50%,
      75% {
        box-shadow: 9984px 0 0 0 ${theme.content.content_dark_3_alpha};
      }

      100% {
        box-shadow: 9984px 15px 0 0 rgba(218, 218, 211, 0);
      }
    }
    @keyframes dot-falling-after {
      0% {
        box-shadow: 10014px -15px 0 0 rgba(218, 218, 211, 0);
      }

      25%,
      50%,
      75% {
        box-shadow: 10014px 0 0 0 ${theme.content.content_dark_3_alpha};
      }

      100% {
        box-shadow: 10014px 15px 0 0 rgba(218, 218, 211, 0);
      }
    }
  `}
`;

export const ChatEvaluation = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
`;

export const EvaluationButton = styled(IconButtonWrapper)`
  ${({ theme }) => css`
    display: flex;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${theme.backgroundColor.bg_light_3_alpha};
  `}
`;

const filterProps = (prop: string) => prop !== 'isLiked';

export const EvaluationLikeIcon = styled(ThumbsUp, {
  shouldForwardProp: filterProps,
})<{
  isLiked: boolean | null;
  size: number;
}>`
  ${({ theme, isLiked, size }) => css`
    width: ${size}px;
    height: ${size}px;
    color: ${isLiked === true
      ? theme.themeColor.main_2
      : theme.content.content_dark_2_alpha};
  `}
`;

export const EvaluationDisLikeIcon = styled(ThumbsDown, {
  shouldForwardProp: filterProps,
})<{
  isLiked: boolean | null;
  size: number;
}>`
  ${({ theme, isLiked, size }) => css`
    width: ${size}px;
    height: ${size}px;
    color: ${isLiked === false
      ? theme.themeColor.negative2
      : theme.content.content_dark_2_alpha};
  `}
`;

export const SystemMessage = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 12px;
    border-radius: 17px;
    background: ${theme.backgroundColor.bg_light_3_alpha};
    gap: 4px;
    color: ${theme.content.content_dark_2_alpha};
    text-align: center;
    ${theme.fontStyle.text.body14_medium};

    &.s {
      padding: 4px 12px;
      border-radius: 8px;
      color: ${theme.content.content_dark_3_alpha};
      ${theme.fontStyle.text.caption_medium};
    }

    span {
      ${theme.fontStyle.text.body14_bold};
    }
  `}
`;
